import { Heading, Text, Tooltip } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useEffect, useRef, useState } from 'react';
import { getCustomerLtv } from 'services/customer';
import { CustomerLtv } from 'services/interfaces/customer';
import { R$ } from 'shared/helpers/currency';
import { useFinancialContext } from '../../FinancialContext';
import ContractDataBox from './ContractDataBox';
import ContractDataSection from './ContractDataSection';
import ContractDetailsBlock from './ContractDetailsBlock';

const TOOLTIP_LABEL =
  'Soma de todas as cobranças pagas do cliente, de todos os contratos - ativos, desativados, cancelados ou excluídos. Sem recorte de período.';

const CustomerLtvCard = () => {
  const { isActive } = useFinancialContext();
  const { customer } = useCustomerContext();
  const [ltv, setLtv] = useState<CustomerLtv | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const loadedCustomerId = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive || !customer?.id) return;
    if (loadedCustomerId.current === customer.id) return;

    loadedCustomerId.current = customer.id;
    setLoading(true);
    setFailed(false);

    getCustomerLtv(customer.id)
      .then((response) => setLtv(response.data))
      .catch((e: AxiosError) => {
        console.error(
          'Erro ao carregar o LTV do cliente',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
        setLtv(null);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [isActive, customer?.id]);

  const paymentDate = (date?: string | null) =>
    date ? moment.utc(date).format('DD/MM/YYYY') : '-';

  return (
    <ContractDataSection
      title={
        <Tooltip label={TOOLTIP_LABEL} hasArrow placement="top-start">
          <Heading variant="small" cursor="default">
            LTV
          </Heading>
        </Tooltip>
      }
    >
      <ContractDetailsBlock removeSeparator={true}>
        <ContractDataBox loading={loading} label="Valor total">
          {failed ? '-' : R$(ltv?.totalPaid ?? 0)}
        </ContractDataBox>
        <ContractDataBox loading={loading} label="Pagamentos">
          {failed ? '-' : (ltv?.ordersCount ?? 0)}
        </ContractDataBox>
        <ContractDataBox loading={loading} label="Primeiro pagamento">
          {paymentDate(ltv?.firstPaymentAt)}
        </ContractDataBox>
        <ContractDataBox loading={loading} label="Último pagamento">
          {paymentDate(ltv?.lastPaymentAt)}
        </ContractDataBox>
      </ContractDetailsBlock>

      {failed && (
        <Text variant="regular" color="yRed.600">
          Não foi possível carregar os pagamentos
        </Text>
      )}
    </ContractDataSection>
  );
};

export default CustomerLtvCard;
